import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { box, label } from './products';
import { makeRailFloorInfill } from './railFloorInfill';
import { addWallBranding } from './wallBranding';
import { addLuxuryArchitecture, trimLuxuryStation } from './luxuryArchitecture';

export function createRoom(scene, renderer, materials, roomName, onChange) {
  scene.background = new THREE.Color('#0a1a17');
  scene.fog = new THREE.Fog('#0f2420', 26, 60);
  const atmosphere = scene.fog;
  const pmrem = new THREE.PMREMGenerator(renderer);
  const envScene = new RoomEnvironment();
  const environment = pmrem.fromScene(envScene).texture;
  scene.environment = environment; envScene.dispose(); pmrem.dispose();
  scene.environmentIntensity = .75;
  const floorMat = materials.showroomFloor;
  const wallMat = materials.galleryWall;
  const darkMat = materials.darkMetal;
  const lightMat = new THREE.MeshBasicMaterial({ color: '#fff1d5', toneMapped: false });
  const floor = box(scene, 34, 0.15, 44, 0, -0.16, 5, floorMat);
  floor.name = 'showroom-oak-herringbone-floor';
  renderer.domElement.dataset.floorStyle = 'bright-oak-herringbone';
  box(scene, 28, 6, 0.3, 0, 2.9, -9, wallMat);
  box(scene, 0.3, 6, 40, -12, 2.9, 8, wallMat);
  box(scene, 0.3, 6, 40, 12, 2.9, 8, wallMat);
  box(scene, 28, 0.16, 1.5, 0, 5.6, -7, materials.architecturalTrim);
  for (const x of [-9, -6, -3, 0, 3, 6, 9]) {
    box(scene, 0.11, 0.2, 32, x, 5.4, 7, materials.architecturalTrim);
    box(scene, 0.04, 0.014, 27, x, 5.29, 6, lightMat);
  }
  const disposeBranding = addWallBranding(scene, renderer, onChange);
  const sub = label('WINDOWS & DOORS', 6.5, 0.45, { color: '#e2b880', size: 40 });
  sub.position.set(0, 2.05, -8.7); scene.add(sub);
  addLuxuryArchitecture(scene, materials);
  // Architectural recesses and daylight glazing.
  for (const x of [-9.5, 9.5]) {
    box(scene, 2, 4.7, 0.03, x, 2.7, -8.8, darkMat);
    box(scene, 1.92, 4.6, 0.04, x, 2.7, -8.75, new THREE.MeshBasicMaterial({ color: '#eee7d6' }));
    for (let y = 1.1; y < 5; y += 1.4) box(scene, 2, 0.025, 0.06, x, y, -8.71, darkMat);
  }
  const ambient = new THREE.HemisphereLight('#e8f2ef', '#53635c', 1.35); scene.add(ambient);
  const sun = new THREE.DirectionalLight('#fff6ea', 2.4); sun.position.set(-5, 11, 7);
  sun.castShadow = true; const shadowSize = renderer.userData?.software ? 512 : 2048; sun.shadow.mapSize.set(shadowSize, shadowSize);
  Object.assign(sun.shadow.camera, { left: -13, right: 13, top: 12, bottom: -12, far: 40 });
  sun.shadow.normalBias = 0.035; sun.shadow.bias = -0.0001; scene.add(sun);
  const fill = new THREE.DirectionalLight('#cadbd5', .8); fill.position.set(7, 5, 0); scene.add(fill);
  const logoLight = new THREE.SpotLight('#ffeed6', 44, 12, .62, .85, 1.5);
  logoLight.position.set(0, 5.2, -5.9); logoLight.target.position.set(0, 2.7, -8.8);
  scene.add(logoLight, logoLight.target);
  let lighting = 'day', inspecting = false;
  const applyLighting = () => {
    const day = lighting === 'day';
    ambient.intensity = inspecting ? 1.5 : day ? 1.35 : .75;
    sun.intensity = inspecting ? 2.7 : day ? 2.4 : 1.1;
    fill.intensity = inspecting ? 1.2 : day ? .8 : .65;
    sun.color.set(inspecting ? '#fffdf5' : '#fff6ea');
    scene.environmentIntensity = inspecting ? .8 : day ? .75 : .55;
    logoLight.intensity = day ? 44 : 60;
    scene.background.set(day ? '#0a1a17' : '#050d0b');
    atmosphere.color.set(day ? '#0f2420' : '#0a1a17');
    renderer.domElement.dataset.lighting = lighting;
  };
  return {
    setLighting: mode => { lighting = mode; applyLighting(); },
    setInspection: enabled => {
      inspecting = enabled; applyLighting();
      // Millimetre-scale profile details need a tight shadow frustum, not the
      // 26 m gallery frustum. This improves contact shadows without extra passes.
      const extent = enabled ? .6 : 13;
      Object.assign(sun.shadow.camera, { left: -extent, right: extent, top: extent, bottom: -extent });
      sun.shadow.normalBias = enabled ? .0005 : .035;
      sun.shadow.bias = enabled ? -.000004 : -.0001;
      sun.target.position.set(0, enabled ? .09 : 0, enabled ? 1.2 : 0);
      sun.target.updateMatrixWorld(); sun.shadow.camera.updateProjectionMatrix();
    },
    dispose: () => { disposeBranding(); environment.dispose(); },
  };
}

export function makeStation(scene, product, index, model, materials) {
  const station = new THREE.Group(); station.position.fromArray(product.position);
  const base = materials.floor;
  const w = Math.max((model.footprint?.width || product.width) + 0.65, 2.6);
  const cx = model.footprint?.centerX || 0;
  let centerInfill = null;
  let railFloorInfill = null;
  if (product.id === 'sd-150') {
    // Leave a real 150 mm recess in the display floor, so a flush sill is not
    // hidden inside a solid slab. The frame occupies this opening.
    for (const z of [-0.35, 0.35]) box(station, w, 0.12, 0.55, 0, 0, z, base);
    const edge = (w - product.width) / 2;
    for (const x of [-1, 1]) box(station, edge, 0.12, 0.15, x * (product.width / 2 + edge / 2), 0, 0, base);
    box(station, product.width, 0.02, 0.15, 0, -0.05, 0, base);
    centerInfill = box(station, product.width, 0.012, 0.016, 0, 0.054, 0, base);
    centerInfill.visible = false;
    // Stop short of the jamb receivers, preserving the actual sash guide slots.
    railFloorInfill = makeRailFloorInfill(product.width - 0.10, base, 0.06);
    station.add(railFloorInfill);
  } else box(station, w, 0.12, 1.25, cx, 0, 0, base);
  model.group.position.y = 0.06 + model.raised + (model.viewHeightOffset || 0); station.add(model.group);
  if (product.is_fixed) box(station, product.width+.45, model.viewHeightOffset, .72, 0, .06+model.viewHeightOffset/2, 0, base);
  if (model.raised) box(station, product.width, model.raised, 0.14, 0, model.raised / 2 + 0.06, -0.04, base);
  trimLuxuryStation(station, w, cx, materials);
  const outline = new THREE.LineSegments(new THREE.EdgesGeometry(new THREE.BoxGeometry(w + 0.08, 0.005, 1.33)), new THREE.LineBasicMaterial({ color: '#c9aa72', transparent: true, opacity: 0.85 }));
  outline.position.set(cx, 0.07, 0); station.add(outline);
  const name = label(`${String(index + 1).padStart(2, '0')}  /  ${product.room_family_name || product.name}`, 1.55, 0.3, { size: 80 });
  name.position.set(cx, 0.025, 0.63); station.add(name);
  // The visible display is a single selection target, including its plinth.
  // This keeps thin fixed frames selectable from the overhead plan as well.
  station.traverse(object => { if (object.isMesh) object.userData.productId=product.id; });
  scene.add(station);
  return { station, outline, centerInfill, railFloorInfill };
}